import { FC, useEffect, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import styled from 'styled-components';
import tw from 'twin.macro';
import strings from '@/language/Swedish';
import { CoordinatesType } from '@/types/dataTypes';
import { AreaPolygons } from '@/api/queries/useIndexPolygons';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from './indexViewerConfig';

const StyledMap = styled.div`
  ${tw`flex relative h-full w-full md:rounded-xl`}

  .googleMap {
    ${tw`md:rounded-xl`}
  }

  .gm-style-mtc,
  .gm-style-cc {
    ${tw`hidden`}
  }
`;

const MapHint = styled.div`
  ${tw`absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-2 rounded-xl border border-white bg-white/90 backdrop-blur shadow-2xl text-xs text-slate-600 pointer-events-none whitespace-nowrap`};
`;

interface MapPolygonType {
  id: string;
  color: string;
  geojson: AreaPolygons['area_polygons'] | null;
}

interface Props {
  polygons: MapPolygonType[];
  marker: CoordinatesType;
  hoveredId?: string | null;
  onMapClick: (coordinates: CoordinatesType) => void;
  onRemoveIndex: (id: string) => void;
  onPolygonHover: (id: string | null) => void;
}

const mapId = import.meta.env.VITE_API_GOOGLE_MAPS_ID;

const IndexMap: FC<Props> = ({
  polygons,
  marker,
  hoveredId,
  onMapClick,
  onRemoveIndex,
  onPolygonHover,
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapHoverId, setMapHoverId] = useState<string | null>(null);
  const layersRef = useRef<Map<string, google.maps.Data>>(new Map());

  // Layer listeners are bound once per layer; refs keep them on fresh callbacks
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const onRemoveIndexRef = useRef(onRemoveIndex);
  onRemoveIndexRef.current = onRemoveIndex;
  const onPolygonHoverRef = useRef(onPolygonHover);
  onPolygonHoverRef.current = onPolygonHover;

  useEffect(() => {
    if (!map) return;
    const layers = layersRef.current;

    for (const [id, layer] of Array.from(layers.entries())) {
      if (!polygons.some((polygon) => polygon.id === id && polygon.geojson)) {
        layer.setMap(null);
        layers.delete(id);
      }
    }

    polygons.forEach((polygon) => {
      if (!polygon.geojson) return;

      const isHighlighted =
        polygon.id === hoveredId || polygon.id === mapHoverId;
      const style = {
        fillColor: polygon.color,
        fillOpacity: isHighlighted ? 0.45 : 0.2,
        strokeColor: polygon.color,
        strokeOpacity: 1,
        strokeWeight: isHighlighted ? 4 : 2,
        zIndex: isHighlighted ? 10 : 1,
        clickable: true,
      };

      const existing = layers.get(polygon.id);
      if (existing) {
        existing.setStyle(style);
        return;
      }

      const layer = new google.maps.Data({ map });
      layer.addGeoJson(polygon.geojson);
      layer.setStyle(style);
      // Clickable polygons swallow map clicks, so forward left-clicks to keep
      // point-picking working inside an area; right-click removes the index
      layer.addListener('click', (event: google.maps.Data.MouseEvent) => {
        if (event.latLng) {
          onMapClickRef.current({
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          });
        }
      });
      layer.addListener('rightclick', () =>
        onRemoveIndexRef.current(polygon.id)
      );
      layer.addListener('mouseover', () => {
        setMapHoverId(polygon.id);
        onPolygonHoverRef.current(polygon.id);
      });
      layer.addListener('mouseout', () => {
        setMapHoverId(null);
        onPolygonHoverRef.current(null);
      });
      layers.set(polygon.id, layer);

      // Pan/zoom so a newly added index area is actually visible
      const bounds = new google.maps.LatLngBounds();
      layer.forEach((feature) =>
        feature
          .getGeometry()
          ?.forEachLatLng((latLng: google.maps.LatLng) => bounds.extend(latLng))
      );
      if (!bounds.isEmpty()) map.fitBounds(bounds);
    });
  }, [map, polygons, hoveredId, mapHoverId]);

  useEffect(() => {
    const layers = layersRef.current;
    return () => {
      layers.forEach((layer) => layer.setMap(null));
      layers.clear();
    };
  }, []);

  // While an index is highlighted, bring its area into view; when the
  // highlight ends, return to the viewport the user had before
  const savedViewRef = useRef<{
    center: google.maps.LatLng;
    zoom: number;
  } | null>(null);

  useEffect(() => {
    if (!map) return;

    if (hoveredId) {
      const layer = layersRef.current.get(hoveredId);
      if (!layer) return;

      const center = map.getCenter();
      const zoom = map.getZoom();
      if (!savedViewRef.current && center && zoom !== undefined) {
        savedViewRef.current = { center, zoom };
      }

      const bounds = new google.maps.LatLngBounds();
      layer.forEach((feature) =>
        feature
          .getGeometry()
          ?.forEachLatLng((latLng: google.maps.LatLng) =>
            bounds.extend(latLng)
          )
      );
      if (!bounds.isEmpty()) map.fitBounds(bounds);
    } else if (savedViewRef.current) {
      map.setCenter(savedViewRef.current.center);
      map.setZoom(savedViewRef.current.zoom);
      savedViewRef.current = null;
    }
  }, [map, hoveredId]);

  return (
    <StyledMap>
      <GoogleMap
        mapContainerClassName="googleMap"
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={(loadedMap) => setMap(loadedMap)}
        onUnmount={() => setMap(null)}
        onClick={(event) => {
          if (event.latLng) {
            onMapClick({ lat: event.latLng.lat(), lng: event.latLng.lng() });
          }
        }}
        options={{
          mapId,
          clickableIcons: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {marker.lat !== null && marker.lng !== null && (
          <MarkerF position={{ lat: marker.lat, lng: marker.lng }} />
        )}
      </GoogleMap>
      <MapHint>{strings.indexApp.mapHint}</MapHint>
    </StyledMap>
  );
};

export default IndexMap;
export type { MapPolygonType };
